import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  addDoc,
  orderBy,
  serverTimestamp,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { Button } from "@mui/material";
import { TextField } from "@mui/material";
import { MenuItem } from "@mui/material";
import { Chip } from "@mui/material";
import { db } from "../../firebase";
import DropDownComponentSorted from "../DropDownComponentSorted";
import { showCustomToast } from "../Misc/CustomToast";
import {
  createMobileMeeting,
  deleteMobileMeeting,
  upsertMobileConfigUser,
  updateMobileUserRole,
} from "../../api/mobileApi";
import { getAuth } from "firebase/auth";
import { CircularProgress } from "@mui/material";
import "./Admin.css";

function Admin(props) {
  const clientID = props.clientId;
  const [, setAlerts] = useState([]);
  const [googleReview, setGoogleReview] = useState(props.googleReview || "");
  const [isLoading, setIsLoading] = useState(false);
  const [recoveryRadio, setRecoveryRadio] = useState(props.recoveryRadio || "");
  const [role, setRole] = useState(props.role || "basic");
  const [mobileUid, setMobileUid] = useState(props.mobileUid || "");
  const [soberSince, setSoberSince] = useState(props.soberSince || ""); // ISO "YYYY-MM-DD"
  const [mTitle, setMTitle] = useState("");
  const [mDate, setMDate] = useState(""); // "YYYY-MM-DD"
  const [mTime, setMTime] = useState(""); // "HH:MM"
  const [meetings, setMeetings] = useState([]); // liste affichée (webapp)
  const [creatingMeeting, setCreatingMeeting] = useState(false);
  const [whatsAppLink, setWhatsAppLink] = useState(props.whatsAppLink || "");
  const [adminDirty, setAdminDirty] = useState(false);
  const [mobileDirty, setMobileDirty] = useState(false);
  const [adminSaveStatus, setAdminSaveStatus] = useState("saved");
  const [mobileSaveStatus, setMobileSaveStatus] = useState("saved");
  const [saveInfo, setSaveInfo] = useState("");
  const [whatsErr, setWhatsErr] = useState(null);
  const [exitErr, setExitErr] = useState(null);

  const isHttpOrHttpsUrl = (s) =>
    !s?.trim() || /^https?:\/\//i.test(s.trim());

  const [exitPlanPdfUrl, setExitPlanPdfUrl] = useState(
    props.exitPlanPdfUrl || "",
  );

  const canCreateMeeting = !!(
    mobileUid.trim() &&
    mTitle.trim() &&
    mDate &&
    mTime
  );
  const calculateDaysDifference = (seconds) => {
    const date1 = new Date(seconds * 1000);
    const date2 = new Date();
    const diffTime = date2 - date1;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleActiveRecoveryChange = (event) => {
    setRecoveryRadio(event);
    setAdminDirty(true);
    setAdminSaveStatus("unsaved");
  };
  const handleGoogleReview = (event) => {
    setGoogleReview(event);
    setAdminDirty(true);
    setAdminSaveStatus("unsaved");
  };

  useEffect(() => {
    if (props.mobileUid && props.mobileUid !== mobileUid) {
      setMobileUid(props.mobileUid);
    }
  }, [props.mobileUid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.role && props.role !== role) {
      setRole(props.role);
    }
  }, [props.role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (clientID) {
      const alertsRef = collection(db, "clients", clientID, "alerts");
      const q = query(alertsRef, where("clientID", "==", clientID));
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const alertsArray = [];
        querySnapshot.forEach((doc) => {
          alertsArray.push({ id: doc.id, ...doc.data() });
        });
        setAlerts(alertsArray);
      });

      return () => unsubscribe();
    }
  }, [clientID]);

  // 👇 Ajoute ceci : écoute des meetings (miroir webapp)
  useEffect(() => {
    if (!clientID) return;
    const q = query(
      collection(db, "clients", clientID, "mobileMeetings"),
      orderBy("startAt", "asc"),
    );
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMeetings(rows);
    });
    return unsub;
  }, [clientID]);

  const [projectedResidentialDischargeDate, ,] = useState(
    props.projectedResidentialDischargeDate || "",
  );
  async function handleSaveAll() {
    let webSaved = false;
    const hasMobileUid = !!mobileUid.trim();
    try {
      const wOk = isHttpOrHttpsUrl(whatsAppLink);
      const eOk = isHttpOrHttpsUrl(exitPlanPdfUrl);
      setWhatsErr(wOk ? null : "Must start with http:// or https://");
      setExitErr(eOk ? null : "Must start with http:// or https://");
      if (!wOk || !eOk) {
        showCustomToast("Please fix URL errors before saving.", "error");
        return;
      }

      setIsLoading(true);
      setSaveInfo("");
      setAdminSaveStatus("saving");
      setMobileSaveStatus(hasMobileUid ? "saving" : "skipped");

      const clientRef = doc(db, "clients", props.clientId);
      await updateDoc(clientRef, {
        recoveryRadio,
        googleReview,
        role: role || "basic",
        mobileUid,
        soberSince: soberSince || null,
        whatsAppLink: whatsAppLink?.trim() || null,
        exitPlanPdfUrl: exitPlanPdfUrl?.trim() || null,
      });
      webSaved = true;
      setAdminSaveStatus("saved");
      setAdminDirty(false);

      if (hasMobileUid) {
        const authWeb = getAuth();
        const user = authWeb.currentUser;
        if (!user) {
          throw new Error("You must be logged in to sync mobile settings.");
        }
        const idToken = await user.getIdToken(true);

        const payload = {
          linkedFrom: "webapp",
          linkedAt: new Date().toISOString(),
          soberSince: soberSince ? new Date(soberSince).toISOString() : null,
          whatsappLink: whatsAppLink?.trim() || null,
          exitPlanUrl: exitPlanPdfUrl?.trim() || null,
        };
        await upsertMobileConfigUser(mobileUid, payload, idToken);

        const rolePayload = {
          uid: mobileUid,
          role: role || "basic",
          roleStatus:
            (role || "basic") === "authorized" ? "approved" : "pending",
        };
        await updateMobileUserRole(rolePayload, idToken);

        setMobileSaveStatus("saved");
        setMobileDirty(false);
        showCustomToast("Saved on webapp + mobile ?", "success");
      } else {
        setMobileSaveStatus("skipped");
        setMobileDirty(false);
        setSaveInfo("Webapp saved. Mobile sync skipped (no Mobile UID).");
        showCustomToast("Webapp saved. Mobile sync skipped (no UID).", "success");
      }
    } catch (e) {
      console.error("[Admin][handleSaveAll] ERROR", e);
      if (!webSaved) {
        setAdminSaveStatus("error");
      }
      if (webSaved && !!mobileUid.trim()) {
        setMobileSaveStatus("error");
        setSaveInfo("Webapp saved, but mobile sync failed.");
      }
      showCustomToast(`Erreur: ${e.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  }

  const hasUnsavedChanges = adminDirty || mobileDirty;
  const statusLabel = (status) => {
    if (status === "saving") return "Saving...";
    if (status === "saved") return "Saved";
    if (status === "unsaved") return "Unsaved changes";
    if (status === "skipped") return "Sync skipped";
    return "Sync failed";
  };
  const statusColor = (status) => {
    if (status === "saved") return "success";
    if (status === "saving") return "info";
    if (status === "unsaved") return "warning";
    if (status === "skipped") return "default";
    return "error";
  };
  async function handleCreateMobileMeeting() {
    try {
      setCreatingMeeting(true);
      if (!mobileUid) {
        showCustomToast("Please fill MobileUID.", "error");
        return;
      }
      if (!mTitle || !mDate || !mTime) {
        showCustomToast("Title, date and hour are required.", "error");
        return;
      }

      const authWeb = getAuth();
      const user = authWeb.currentUser;
      if (!user) {
        showCustomToast("User must be connected", "error");
        return;
      }
      const idToken = await user.getIdToken(true);

      const startAtISO = new Date(`${mDate}T${mTime}:00`).toISOString();

      // await createMobileMeeting(
      const { id: mobileId } = await createMobileMeeting(
        { uid: mobileUid, title: mTitle, startAtISO, email: props.email },
        idToken,
      );

      // 2) Miroir côté WEBAPP (pour l’affichage Admin)
      await addDoc(
        collection(db, "clients", props.clientId, "mobileMeetings"),
        {
          mobileUid,
          title: mTitle,
          startAt: new Date(startAtISO), // Firestore => Timestamp
          startAtISO,
          mobileId,
          createdAt: serverTimestamp(),
        },
      );

      showCustomToast("Meeting created on mobile side ✅", "success");
      setMTitle("");
      setMDate("");
      setMTime("");
    } catch (e) {
      console.error(e);
      showCustomToast(`Erreur création meeting: ${e.message}`, "error");
    } finally {
      setCreatingMeeting(false);
    }
  }

  // async function handleDeleteMeeting(meetingId, startAtISO) {
  async function handleDeleteMeeting(meetingId, mobileId) {
    try {
      const authWeb = getAuth();
      const user = authWeb.currentUser;
      if (!user) {
        showCustomToast("You should be connected.", "error");
        return;
      }
      const idToken = await user.getIdToken(true);

      // 1) Supprime dans le Firestore MOBILE via API sécurisée
      // await deleteMobileMeeting(mobileUid, startAtISO, idToken);
      await deleteMobileMeeting(mobileUid, mobileId, idToken);

      // 2) Supprime côté WEBAPP Firestore
      const meetingRef = doc(
        db,
        "clients",
        props.clientId,
        "mobileMeetings",
        meetingId,
      );
      await deleteDoc(meetingRef);

      showCustomToast("Meeting deleted ✅", "success");
    } catch (e) {
      console.error(e);
      showCustomToast(`Error deleting meeting: ${e.message}`, "error");
    }
  }

  return (
    <div
      className="tab-container-white admin-panel"
      style={{
        paddingLeft: "10px",
        paddingRight: "10px",
        paddingBottom: "60px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <div className="tab-container-title">Administrator extra infos</div>
        <Chip
          size="small"
          label={`Webapp: ${statusLabel(adminSaveStatus)}`}
          color={statusColor(adminSaveStatus)}
          variant={adminSaveStatus === "saved" ? "filled" : "outlined"}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "row" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            paddingBottom: 30,
          }}
        >
          {/* <div style={{ marginLeft: 15 }}>In active recovery?</div> */}
          <div style={{ width: 150 }}>
            <DropDownComponentSorted
              api="yesno"
              title="In active recovery?"
              value={recoveryRadio}
              onChange={handleActiveRecoveryChange}
            />
          </div>

          {recoveryRadio === "Yes" && (
            <div style={{ marginLeft: 15, marginTop: 10, color: "red" }}>
              Total time in recovery:{" "}
              {calculateDaysDifference(
                projectedResidentialDischargeDate.seconds,
              )}{" "}
              days
            </div>
          )}
        </div>
        <DropDownComponentSorted
          api="yesno"
          title="Google review done?"
          value={googleReview}
          onChange={handleGoogleReview}
        />
      </div>

      <div
        style={{
          border: "1px solid lightgrey",
          borderRadius: "20px",
          paddingBottom: "20px",
          paddingLeft: "10px",
          paddingRight: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div className="tab-container-title">Mobile app Setting</div>
          <Chip
            size="small"
            label={`Mobile: ${statusLabel(mobileSaveStatus)}`}
            color={statusColor(mobileSaveStatus)}
            variant={mobileSaveStatus === "saved" ? "filled" : "outlined"}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignContent: "flex-start",
            alignItems: "flex-start",
            paddingLeft: "10px",
            paddingRight: "10px",
            border: "1px solid lightgrey",
            borderRadius: "10px",
            paddingBottom: "10px",
            marginTop: "15px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignContent: "flex-start",
              gap: "10px",
            }}
          >
            <div
              style={{
                marginTop: 8,
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
                paddingLeft: "10px",
                gap: 8,
              }}
            >
              <div style={{ fontSize: "12px" }}>Mobile UID</div>
              <input
                type="text"
                value={mobileUid}
                onChange={(e) => {
                  setMobileUid(e.target.value);
                  setMobileDirty(true);
                  setMobileSaveStatus("unsaved");
                }}
                placeholder="Mobile user UID"
                style={{ width: 280, height: 40 }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "row" }}>
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div style={{ fontSize: "12px" }}>Sober since</div>
                <input
                  type="date"
                  value={soberSince}
                  onChange={(e) => {
                    setSoberSince(e.target.value);
                    setMobileDirty(true);
                    setMobileSaveStatus("unsaved");
                  }}
                  style={{ width: 180, height: 40 }}
                />
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
              paddingLeft: "10px",
              gap: 8,
            }}
          >
            <div style={{ fontSize: "12px" }}>Role</div>
            <TextField
              select
              value={role}
              onChange={(e) => {
                setRole(e.target.value);
                setMobileDirty(true);
                setMobileSaveStatus("unsaved");
              }}
              size="small"
              disabled={!mobileUid.trim()}
              sx={{ width: 220 }}
            >
              <MenuItem value="basic">basic</MenuItem>
              <MenuItem value="authorized">authorized</MenuItem>
            </TextField>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "20px",
              padding: "10px 10px 0 10px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                width: 420,
              }}
            >
              <label style={{ fontSize: 12 }}>
                WhatsApp group link (full URL with http://)
              </label>
              <input
                type="url"
                value={whatsAppLink}
                // onChange={(e) => setWhatsAppLink(e.target.value)}
                onChange={(e) => {
                  const v = e.target.value;
                  setWhatsAppLink(v);
                  setMobileDirty(true);
                  setMobileSaveStatus("unsaved");
                  setWhatsErr(
                    isHttpOrHttpsUrl(v)
                      ? null
                      : "Must start with http:// or https://",
                  );
                }}
                placeholder="https://chat.whatsapp.com/..."
                style={{ height: 40 }}
              />
              {whatsErr && (
                <div style={{ color: "#b71c1c", fontSize: 12, marginTop: 4 }}>
                  {whatsErr}
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                width: 420,
              }}
            >
              <label style={{ fontSize: 12 }}>
                Exit plan PDF link (web URL with http://)
              </label>
              <input
                type="url"
                value={exitPlanPdfUrl}
                // onChange={(e) => setExitPlanPdfUrl(e.target.value)}
                onChange={(e) => {
                  const v = e.target.value;
                  setExitPlanPdfUrl(v);
                  setMobileDirty(true);
                  setMobileSaveStatus("unsaved");
                  setExitErr(
                    isHttpOrHttpsUrl(v)
                      ? null
                      : "Must start with http:// or https://",
                  );
                }}
                placeholder="https://example.com/exit-plan.pdf"
                style={{ height: 40 }}
              />
              {exitErr && (
                <div style={{ color: "#b71c1c", fontSize: 12, marginTop: 4 }}>
                  {exitErr}
                </div>
              )}
            </div>
          </div>

          <div style={{ width: "100%", paddingLeft: "10px", paddingTop: "6px" }}>
            <div style={{ color: "#607d8b", fontSize: 12 }}>
              Use the single global save button below to persist webapp data and
              sync mobile settings.
            </div>
            {saveInfo ? (
              <div style={{ marginTop: 6, color: "#607d8b", fontSize: 12 }}>
                {saveInfo}
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
          <div
            style={{
              border: "1px solid lightgrey",
              borderRadius: "10px",
              marginTop: "10px",
              paddingLeft: "0px",
              paddingRight: "20px",
              paddingBottom: "10px",
              width: "50%",
            }}
          >
            <div
              style={{
                marginTop: 0,
                // border: "1px solid #e0e0e0",
                borderRadius: 10,
                paddingLeft: "20px",
                paddingTop: "20px",
              }}
            >
              <div style={{ fontSize: "16px", fontWeight: 500 }}>
                Mobile meetings (web mirror)
              </div>
              {meetings.length === 0 ? (
                <div style={{ color: "#777", marginTop: 8 }}>
                  No meetings yet.
                </div>
              ) : (
                <ul style={{ marginTop: 8, paddingLeft: 18 }}>
                  {meetings.map((m) => {
                    const d = m.startAt?.toDate
                      ? m.startAt.toDate()
                      : new Date(m.startAt);
                    const when =
                      d?.toLocaleDateString?.([], {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      }) +
                      " " +
                      d?.toLocaleTimeString?.([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      });
                    return (
                      <div
                        key={m.id}
                        style={{
                          marginBottom: 6,
                          border: "1px solid lightgrey",
                          paddingLeft: "10px",
                          width: "auto",
                          borderRadius: "5px",
                        }}
                      >
                        <strong>{m.title}</strong> —{" "}
                        <span style={{ color: "#555", paddingRight: "10px" }}>
                          {when}
                        </span>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          // onClick={() =>
                          //   handleDeleteMeeting(m.id, m.startAtISO)
                          // }
                          onClick={() => handleDeleteMeeting(m.id, m.mobileId)}
                          disabled={!m.mobileId}
                        >
                          Delete
                        </Button>
                      </div>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
          <div
            style={{
              border: "1px solid lightgrey",
              borderRadius: "10px",
              marginTop: "10px",
              paddingLeft: "20px",
              paddingRight: "20px",
              paddingBottom: "10px",
              width: "50%",
            }}
          >
            <div
              style={{
                marginTop: 16,
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <TextField
                label="Title"
                placeholder="Meeting title"
                value={mTitle}
                onChange={(e) => setMTitle(e.target.value)}
                disabled={!mobileUid.trim()}
                size="small"
                sx={{ width: 260 }}
              />
              <TextField
                label="Date"
                type="date"
                value={mDate}
                onChange={(e) => setMDate(e.target.value)}
                disabled={!mobileUid.trim()}
                size="small"
                sx={{ width: 180 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Time"
                type="time"
                value={mTime}
                onChange={(e) => setMTime(e.target.value)}
                disabled={!mobileUid.trim()}
                size="small"
                sx={{ width: 140 }}
                InputLabelProps={{ shrink: true }}
              />

              <Button
                variant="contained"
                onClick={handleCreateMobileMeeting}
                disabled={creatingMeeting || !canCreateMeeting}
                style={{ marginLeft: 8, minWidth: 210 }}
              >
                {creatingMeeting ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  "Add meeting on mobile"
                )}
              </Button>
            </div>
            {!mobileUid && (
              <div style={{ color: "#b71c1c", marginTop: 8 }}>
                Enter & save a <b>Mobile UID</b> before adding meetings.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="button-container-form-gi">
        <Button
          variant={hasUnsavedChanges ? "contained" : "outlined"}
          onClick={handleSaveAll}
          style={
            isLoading
              ? {
                  backgroundColor: "red",
                  color: "white",
                  marginLeft: "15px",
                }
              : {
                  backgroundColor: hasUnsavedChanges ? "#1565C0" : undefined,
                  color: hasUnsavedChanges ? "white" : undefined,
                  marginLeft: "15px",
                }
          }
          disabled={isLoading || !hasUnsavedChanges}

          sx={{
            height: "40px",
            width: "320px",
            marginLeft: "25px",
            marginRight: "30px",
          }}
        >
          {isLoading ? "SAVING WEBAPP + MOBILE..." : "SAVE ALL CHANGES"}
        </Button>
      </div>
    </div>
  );
}

export default Admin;

